// src/pages/DebtStudio/reports/exportDebtStudioIssuePdf.js

import dayjs from "lib/dayjs";
import { createCompassPdf } from "reporting/pdf/createCompassPdf";
import { addStatistics } from "reporting/pdf/addStatistics";
import { addSourcesUses } from "reporting/pdf/addSourcesUses";
import { addReportTable } from "reporting/pdf/addReportTable";
import { addKpiCards } from "reporting/pdf/addKpiCards";
import { addDebtServiceChart } from "reporting/pdf/addDebtServiceChart";
import { addSectionTitle, ensurePageSpace } from "reporting/pdf/pdfLayout";
import { safeFilePart } from "reporting/pdf/pdfFormatters";
import { buildIssueReportData } from "./buildIssueReportData";

function addIssueHeader(ctx, data, startY) {
  const { doc, margins, usableWidth } = ctx;

  const issuer = data?.meta?.issuer || "—";
  const issueName = data?.meta?.issueName || "—";

  let y = startY;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(20);
  doc.text("Issuer Name", margins.left, y);

  doc.setFont("helvetica", "normal");
  doc.text(String(issuer), margins.left + 85, y, {
    maxWidth: usableWidth - 85,
  });

  y += 16;

  doc.setFont("helvetica", "bold");
  doc.text("Issue Name", margins.left, y);

  doc.setFont("helvetica", "normal");
  doc.text(String(issueName), margins.left + 85, y, {
    maxWidth: usableWidth - 85,
  });

  return y + 30;
}

function addCapitalizedInterestSection(ctx, capitalizedInterest, startY) {
  if (!capitalizedInterest?.show) return startY;

  let y = ensurePageSpace(ctx, startY, 130);

  y = addSectionTitle(ctx, "Capitalized Interest", y);
  y = addKpiCards(ctx, capitalizedInterest.kpis || [], y);

  if (capitalizedInterest.table?.rows?.length) {
    y = addReportTable(ctx, capitalizedInterest.table, y);
  }

  return y;
}

export async function exportDebtStudioIssuePdf({
  reportData = null,
  selectedReports = null,
  save = true,
  ...builderArgs
} = {}) {
  const data = reportData || buildIssueReportData(builderArgs);

  const isLibraryIssue = !!data?.meta?.isLibraryIssue;

  const ctx = await createCompassPdf({
    orgId: builderArgs.orgId,
    reportTitle: data.meta.reportTitle,
    orgName: data.meta.orgName || data.meta.issuer,
    asOfLabel: data.meta.asOfLabel,
    createdStamp: new Date(),
    branding: "unbranded",
  });

  const { doc, margins } = ctx;
  const selected = new Set(
    Array.isArray(selectedReports)
      ? selectedReports
      : ["sources", "stats", "summary", "debt", "coi", "capi"]
  );

  let y = margins.top + 10;
  y = addIssueHeader(ctx, data, y);
  let renderedComponent = false;

  const beginComponent = () => {
    if (renderedComponent) {
      doc.addPage();
      y = margins.top;
    }
    renderedComponent = true;
  };

  if (!isLibraryIssue && selected.has("sources")) {
    beginComponent();
    y = addSourcesUses(ctx, data.sourcesUses, y);
  }

  if (!isLibraryIssue && selected.has("stats")) {
    beginComponent();
    if (data.refundingStatisticsSections?.length) {
      y = addStatistics(
        ctx,
        {
          title: "Refunding Results",
          sections: data.refundingStatisticsSections,
          columns: 1,
          sectionHeight: 145,
        },
        y
      );
    }
    y = addStatistics(
      ctx,
      {
        title: "Issue Statistics",
        sections: data.issueStatisticsSections,
        columns: 2,
        sectionHeight: 100,
      },
      y
    );
  }

  if (selected.has("summary") && data.structureTable?.show !== false) {
    beginComponent();
    y = addReportTable(ctx, data.structureTable, y);
  }

  if (selected.has("debt")) {
    beginComponent();
    y = addKpiCards(ctx, data.debtServiceKpis || [], y);
    if (!data?.meta?.isLoan) {
      y = addDebtServiceChart(
        ctx,
        {
          title: "Debt Service Chart",
          rows: data.debtServiceChart?.rows || data.debtServiceTable?.rows || [],
        },
        y
      );
    }
    y = addReportTable(ctx, data.debtServiceTable, y);
    if (data.refundingComparisonTable?.rows?.length) {
      y = addReportTable(ctx, data.refundingComparisonTable, y);
    }
  }

  if (selected.has("coi") && data.costOfIssuanceTable) {
    beginComponent();
    y = addReportTable(ctx, data.costOfIssuanceTable, y);
  }

  if (selected.has("capi") && data.capitalizedInterest?.show) {
    beginComponent();
    addCapitalizedInterestSection(ctx, data.capitalizedInterest, y);
  }

  ctx.finalize();

  const fileName = `DebtStudio_Issue_${safeFilePart(
    data.meta.issuer
  )}_${safeFilePart(data.meta.issueName)}_${dayjs(data.meta.fileDate).format(
    "YYYY-MM-DD"
  )}.pdf`;

  if (save) {
    doc.save(fileName);
  }

  return {
    doc,
    fileName,
    reportData: data,
  };
}

export default exportDebtStudioIssuePdf;
